<template>
  <div class="license-form">
    <div class="license-form__header">
      <div>
        <h2 class="license-form__title">
          {{ editing ? $t('Edit License') : $t('Create License') }}
        </h2>

        <p class="license-form__description">
          {{
            editing
                ? $t('Update the license details below.')
                : $t('Enter the details for the new license.')
          }}
        </p>
      </div>

      <div class="license-form__badge">
        {{ editing ? $t('Editing') : $t('New') }}
      </div>
    </div>

    <form @submit.prevent="submitForm">
      <div class="license-form__grid">
        <div class="form-field form-field--wide">
          <label for="license-name">
            {{ $t('Name') }}
            <span class="required">*</span>
          </label>

          <input
              id="license-name"
              v-model.trim="form.name"
              type="text"
              required
              autocomplete="off"
              :placeholder="$t('Enter a license name')"
          />
        </div>

        <div class="form-field">
          <label for="license-start-date">
            {{ $t('Start Date') }}
            <span class="required">*</span>
          </label>

          <input
              id="license-start-date"
              v-model="form.startDate"
              type="date"
              required
          />
        </div>

        <div class="form-field">
          <label for="license-expiration-date">
            {{ $t('Expiration Date') }}
            <span class="required">*</span>
          </label>

          <input
              id="license-expiration-date"
              v-model="form.expirationDate"
              :min="form.startDate || null"
              type="date"
              required
          />

          <span
              v-if="expirationDateInvalid"
              class="form-field__error"
          >
            {{ $t('Expiration date must be after the start date.') }}
          </span>
        </div>

        <div class="form-field">
          <label for="license-type">
            {{ $t('Type') }}
            <span class="required">*</span>
          </label>

          <select
              id="license-type"
              v-model="form.type"
              required
          >
            <option disabled value="">
              {{ $t('Select license type') }}
            </option>
            <option value="viewer">{{ $t('Viewer') }}</option>
            <option value="editor">{{ $t('Editor') }}</option>
            <option value="manager">{{ $t('Manager') }}</option>
            <option value="admin">{{ $t('Admin') }}</option>
          </select>
        </div>


        <div class="form-field form-field--wide">
          <label for="license-user">
            {{ $t('User') }}
          </label>

          <searchable-user-select
              v-model="form.userId"
              :users="users"
              :placeholder="$t('Search or select a user…')"
          />

          <span class="form-field__hint">
            {{ $t('Leave empty for an unassigned license.') }}
          </span>
        </div>
      </div>

      <div class="license-form__actions">
        <button
            type="button"
            class="license-button license-button--secondary"
            @click="$emit('abort')"
        >
          {{ $t('Abort') }}
        </button>

        <button
            type="submit"
            class="license-button license-button--primary"
            :disabled="expirationDateInvalid"
        >
          {{ editing ? $t('Save changes') : $t('Create license') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import SearchableUserSelect from './SearchableUserSelect.vue';

const createEmptyForm = () => ({
  id: null,
  name: '',
  startDate: '',
  expirationDate: '',
  type: 'viewer',
  userId: ''
});

export default {
  name: 'LicenseForm',

  components: {
    SearchableUserSelect
  },

  props: {
    license: {
      type: Object,
      default: createEmptyForm
    },

    users: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      form: {
        ...createEmptyForm(),
        ...this.license
      }
    };
  },

  computed: {
    editing() {
      return Boolean(this.form.id);
    },

    expirationDateInvalid() {
      if (!this.form.startDate || !this.form.expirationDate) {
        return false;
      }

      return this.form.expirationDate < this.form.startDate;
    }
  },

  watch: {
    license: {
      deep: true,
      handler(license) {
        this.form = {
          ...createEmptyForm(),
          ...license
        };
      }
    }
  },

  methods: {
    submitForm() {
      if (this.expirationDateInvalid) {
        return;
      }

      let args = {...this.form};
      if(!this.form.id) {
        delete args.id;
      }

      this.$emit('submit', args);
    }
  }
};
</script>

<style scoped lang="scss">
.license-form {
  max-width: 900px;
  margin: 24px 0;
  padding: 28px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.license-form__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
}

.license-form__title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.license-form__description {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 14px;
}

.license-form__badge {
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.license-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-field--wide {
  grid-column: 1 / -1;
}

.form-field label {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
}

.form-field input,
.form-field select {
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 7px;
  background: rgba(0, 0, 0, 0.2);
  color: inherit;
  font: inherit;
  transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease,
      background 0.15s ease;
}

.form-field input:hover,
.form-field select:hover {
  border-color: rgba(255, 255, 255, 0.32);
}

.form-field input:focus,
.form-field select:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.65);
  background: rgba(0, 0, 0, 0.28);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.08);
}

.form-field__hint {
  margin-top: 7px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
}

.form-field__error {
  margin-top: 7px;
  color: #ff6b6b;
  font-size: 12px;
}

.required {
  color: #ff6b6b;
}


.license-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.license-button {
  min-width: 120px;
  min-height: 42px;
  padding: 9px 18px;
  border: 1px solid transparent;
  border-radius: 7px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition:
      transform 0.15s ease,
      opacity 0.15s ease,
      background 0.15s ease;
}

.license-button:hover:not(:disabled) {
  transform: translateY(-1px);
}

.license-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.license-button--primary {
  background: #ffffff;
  color: #222222;
}

.license-button--primary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.88);
}

.license-button--secondary {
  border-color: rgba(255, 255, 255, 0.2);
  background: transparent;
  color: inherit;
}

.license-button--secondary:hover {
  background: rgba(255, 255, 255, 0.08);
}

@media (max-width: 700px) {
  .license-form {
    padding: 20px;
  }

  .license-form__header {
    flex-direction: column;
  }

  .license-form__grid {
    grid-template-columns: 1fr;
  }

  .form-field--wide {
    grid-column: auto;
  }

  .license-form__actions {
    flex-direction: column-reverse;
  }

  .license-button {
    width: 100%;
  }
}
</style>